/**
 * useDrafts — сохранение черновиков сообщений на сервер.
 *
 * Минимальная надёжная версия:
 * 1. При mount — GET /chat/drafts, все черновики юзера в Map.
 * 2. При открытии топика с draft'ом — подставляем в composer.
 * 3. При изменении text/replyTo — debounced-save 700мс.
 * 4. При смене топика (cleanup) — force-flush текущий текст для уходящего.
 * 5. При закрытии вкладки (beforeunload) — flush через fetch keepalive.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import type { QueryClient } from "@tanstack/react-query";
import {
  fetchChatDrafts, saveChatDraft,
  fetchChatMessagesAround,
  type ChatMessage, type ChatReplyPreview,
} from "@/api/dashboard";

type DraftEntry = { text: string; reply_to_id: number | null };

export type UseDraftsParams = {
  activeTopicId: number | null;
  userId: string | undefined;
  qc: QueryClient;
  text: string;
  replyTo: ChatReplyPreview | null;
  setText: (s: string) => void;
  setReplyTo: (r: ChatReplyPreview | null) => void;
};

const DEBOUNCE_MS = 700;

export function useDrafts(params: UseDraftsParams) {
  const { activeTopicId, userId, qc, text, replyTo, setText, setReplyTo } = params;
  const [draftsByTopic, setDraftsByTopic] = useState<Map<number, DraftEntry>>(() => new Map());

  // Refs — доступны в timer'ах, cleanup'ах и beforeunload
  const textRef = useRef(text);
  const replyToRef = useRef(replyTo);
  const activeTopicRef = useRef<number | null>(activeTopicId);
  const timerRef = useRef<number | null>(null);
  useEffect(() => { textRef.current = text; }, [text]);
  useEffect(() => { replyToRef.current = replyTo; }, [replyTo]);
  useEffect(() => { activeTopicRef.current = activeTopicId; }, [activeTopicId]);

  // Прямая отправка на сервер + локальное обновление Map
  const doSave = useCallback((tid: number, t: string, r: number | null) => {
    const isEmpty = !t.trim() && r === null;
    setDraftsByTopic(prev => {
      const next = new Map(prev);
      if (isEmpty) next.delete(tid); else next.set(tid, { text: t, reply_to_id: r });
      return next;
    });
    void saveChatDraft(tid, t, r).catch(() => { /* silent */ });
  }, []);

  // 1. Hydration: один GET /chat/drafts на mount.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetchChatDrafts().then((rows) => {
      if (cancelled) return;
      const m = new Map<number, DraftEntry>();
      for (const r of rows) m.set(r.topic_id, { text: r.text, reply_to_id: r.reply_to_id });
      setDraftsByTopic(m);
      // Если открытый топик уже совпадает — подставляем draft в composer
      const tid = activeTopicRef.current;
      if (tid) {
        const d = m.get(tid);
        if (d && !textRef.current.trim() && !replyToRef.current) {
          setText(d.text);
          if (d.reply_to_id) hydrateReplyPreview(qc, tid, d.reply_to_id, setReplyTo);
        }
      }
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 2. При СМЕНЕ топика: сначала flush старого, потом подставить draft нового.
  useEffect(() => {
    const tid = activeTopicId;
    if (tid) {
      const d = draftsByTopic.get(tid);
      if (d) {
        setText(d.text);
        if (d.reply_to_id) hydrateReplyPreview(qc, tid, d.reply_to_id, setReplyTo);
        else setReplyTo(null);
      } else {
        setText("");
        setReplyTo(null);
      }
    } else {
      setText("");
      setReplyTo(null);
    }
    return () => {
      // Cleanup при смене — force flush уходящего топика (не ждём debounce)
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const leavingTid = tid;
      if (leavingTid) {
        const t = textRef.current;
        const r = replyToRef.current?.id ?? null;
        // Сохраняем текущее состояние (может быть пустым — тогда сервер удалит)
        doSave(leavingTid, t, r);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicId]);

  // 2b. Rehydration: если draftsByTopic прилетел ПОСЛЕ topic-switch race'а.
  useEffect(() => {
    if (!activeTopicId) return;
    const d = draftsByTopic.get(activeTopicId);
    if (!d) return;
    // Composer уже занят — не перезаписываем
    if (textRef.current.trim() || replyToRef.current) return;
    setText(d.text);
    if (d.reply_to_id) hydrateReplyPreview(qc, activeTopicId, d.reply_to_id, setReplyTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopicId, draftsByTopic]);

  // 3. Debounced save при изменении text/replyTo.
  useEffect(() => {
    if (!activeTopicId || !userId) return;
    const tid = activeTopicId;
    const t = text;
    const r = replyTo?.id ?? null;
    // Cleanup таймера + новый debounce
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      doSave(tid, t, r);
      timerRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [text, replyTo, activeTopicId, userId, doSave]);

  // 4. beforeunload — при закрытии вкладки force-flush через fetch keepalive.
  useEffect(() => {
    const flush = () => {
      const tid = activeTopicRef.current;
      if (!tid) return;
      const t = textRef.current;
      const r = replyToRef.current?.id ?? null;
      try {
        fetch(`/api/chat/topics/${tid}/draft`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ text: t, reply_to_id: r }),
          keepalive: true,
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  // Cleanup из sendMut.onSuccess.
  const clearDraftForTopic = useCallback((topicId: number) => {
    setDraftsByTopic(prev => {
      if (!prev.has(topicId)) return prev;
      const next = new Map(prev);
      next.delete(topicId);
      return next;
    });
  }, []);

  return { clearDraftForTopic, draftsByTopic };
}

// ── helpers ────────────────────────────────────────────────────────────
function hydrateReplyPreview(
  qc: QueryClient,
  tid: number,
  replyId: number,
  setReplyTo: (r: ChatReplyPreview | null) => void,
): void {
  const cached = (qc.getQueryData<ChatMessage[]>(["chat/messages", tid]) ?? [])
    .find(x => x.id === replyId);
  if (cached) {
    setReplyTo({
      id: cached.id,
      author_name: cached.author_name,
      preview: (cached.text || "").replace(/\n/g, " ").slice(0, 120),
      deleted: !!cached.deleted_at,
    });
    return;
  }
  fetchChatMessagesAround(tid, replyId, 120).then(slice => {
    const target = slice.find(x => x.id === replyId);
    if (target) {
      setReplyTo({
        id: target.id,
        author_name: target.author_name,
        preview: (target.text || "").replace(/\n/g, " ").slice(0, 120),
        deleted: !!target.deleted_at,
      });
    }
  }).catch(() => { /* ignore */ });
  setReplyTo(null);
}
